/**
 * screens/queueDetail.tsx — Queue Detail (rev 5). ONE page for the selected
 * queue: header (back · title + match badge · subtitle · count cards · sub-tabs)
 * then Overview / Controls / Setup. Faithful to the prototype's qdetail header.
 * Overview reads GET /v1/queues/:id/overview (polled); Controls/Setup reuse the
 * Settings screen scoped by section.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useTheme } from '../context/themeContext';
import { Mono } from '../queue/components/atoms';
import { SurfaceFill } from '../lib/surfaceLayout';
import { useActiveQueue } from '../queue/activeQueue';
import { useSurfaceRouter } from '../navigation/surfaceRouter';
import { useSession } from '../context/sessionContext';
import { useDataSource } from '../queue/dataSource';
import { getOverview } from '../services/queue/queueService';
import { useQueueStream } from './hooks/useQueueStream';
import { handleApiError } from '../utils/errorHandler';
import OverviewPanel from './detail/overviewPanel';
import SettingsScreen from './settings';
import type { OverviewView } from '../api/queueDtos';

type SubTab = 'overview' | 'controls' | 'setup';

export default function QueueDetailScreen() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { mode } = useDataSource();
  const { activeQueue } = useActiveQueue();
  const { setSurface } = useSurfaceRouter();
  const { accessLevel } = useSession();
  const [tab, setTab] = useState<SubTab>('overview');
  const [overview, setOverview] = useState<OverviewView | null>(null);

  // Setup is Softknack-only (compose-time config). A client admin sees only
  // Overview + Controls — real role from the session, no demo switcher.
  const isSoftknack = accessLevel === 'softknack';
  const tabs: SubTab[] = isSoftknack ? ['overview', 'controls', 'setup'] : ['overview', 'controls'];
  // Guard: if a non-Softknack session somehow lands on 'setup', fall back.
  const activeTab: SubTab = tab === 'setup' && !isSoftknack ? 'overview' : tab;

  const queueId = activeQueue?.id ?? null;
  const load = useCallback(() => {
    if (mode !== 'live' || !queueId) { setOverview(null); return; }
    getOverview(queueId).then(setOverview).catch((err) => handleApiError(err, 'Could not load overview'));
  }, [mode, queueId]);
  // SSE-pushed refresh; poll is the fallback (slow when the stream is live).
  const { connected } = useQueueStream(mode === 'live' ? queueId : null, load);
  useEffect(() => {
    load();
    const t = setInterval(load, connected ? 30000 : 5000);
    return () => clearInterval(t);
  }, [load, connected]);

  if (!activeQueue) {
    return (
      <SurfaceFill>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: colors.ink4, marginBottom: 12 }}>No queue selected.</Text>
          <TouchableOpacity onPress={() => setSurface('dashboard')}
            style={{ paddingHorizontal: 16, height: 38, borderRadius: 10, borderWidth: 1, borderColor: colors.lineStrong, justifyContent: 'center' }}>
            <Text style={{ color: colors.ink, fontWeight: '600' }}>← Dashboard</Text>
          </TouchableOpacity>
        </View>
      </SurfaceFill>
    );
  }

  const fitted = activeQueue.matchType === 'FITTED';
  const resourceNoun = (overview?.queue.vocab?.resource) || (fitted ? 'Table' : 'Slot');
  const counts = overview?.counts;
  const countCards = [
    { k: 'In line', v: counts ? String(counts.inLine) : '—' },
    { k: 'In service', v: counts ? String(counts.inService) : '—' },
    { k: `${resourceNoun[0].toUpperCase()}${resourceNoun.slice(1)}s free`, v: counts ? String(counts.free) : '—' },
  ];

  return (
    <SurfaceFill>
      <View style={{ width: '100%', maxWidth: 1180 }}>
        {/* back */}
        <TouchableOpacity onPress={() => setSurface('dashboard')} style={{ marginBottom: 12 }}>
          <Text style={{ color: colors.ink3, fontSize: 13 }}>← Dashboard</Text>
        </TouchableOpacity>

        {/* title row + count cards */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ fontSize: 26, fontWeight: '700', color: colors.ink }}>{activeQueue.name}</Text>
              <View style={{ height: 22, paddingHorizontal: 10, borderRadius: 6, justifyContent: 'center', backgroundColor: fitted ? colors.ink : colors.card, borderWidth: fitted ? 0 : 1, borderColor: colors.ink }}>
                <Mono style={{ fontSize: 11, fontWeight: '600', color: fitted ? colors.white : colors.ink }}>{activeQueue.matchType}</Mono>
              </View>
            </View>
            <Mono style={{ fontSize: 13, color: colors.ink3, marginTop: 4 }}>generic queue view · everything about this queue, one page</Mono>
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {countCards.map((c) => (
              <View key={c.k} style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 11, paddingVertical: 10, paddingHorizontal: 14, minWidth: 78 }}>
                <Mono style={{ fontSize: 20, fontWeight: '600', lineHeight: 22, color: colors.ink }}>{c.v}</Mono>
                <Text style={{ fontSize: 10, color: colors.ink4, marginTop: 4 }}>{c.k}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* sub-tabs — Setup only for Softknack (real role, no demo switcher) */}
        <View style={{ flexDirection: 'row', gap: 7, marginTop: 18, borderBottomWidth: 1, borderBottomColor: colors.line, paddingBottom: 16, flexWrap: 'wrap' }}>
          {tabs.map((t) => {
            const active = activeTab === t;
            return (
              <TouchableOpacity key={t} onPress={() => setTab(t)}
                style={{ paddingHorizontal: 15, height: 34, borderRadius: 8, justifyContent: 'center', backgroundColor: active ? colors.ink : colors.card, borderWidth: active ? 0 : 1, borderColor: colors.line }}>
                <Text style={{ fontSize: 13, fontWeight: '500', textTransform: 'capitalize', color: active ? colors.white : colors.ink3 }}>{t}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      {/* body */}
      <View style={{ flex: 1, minHeight: 0 as any, width: '100%', maxWidth: 1180, marginTop: 4 }}>
        {activeTab === 'overview' && <OverviewPanel data={overview} product={activeQueue.product} />}
        {activeTab === 'controls' && <SettingsScreen section="controls" />}
        {activeTab === 'setup' && <SettingsScreen section="setup" />}
      </View>
    </SurfaceFill>
  );
}
